import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppErrorFallback } from '@/components/AppErrorFallback';

/** Where "Go to dashboard" sends the user. Matches the NotFound page. */
const HOME_ROUTE = '/dashboard';

/**
 * The application's primary error boundary. Mounted inside the Router so the
 * fallback can navigate, which is what makes recovery possible without a
 * browser reload.
 */
export function RoutedErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <ErrorBoundary
      resetKey={location.pathname}
      fallback={({ error, reset }) => (
        <AppErrorFallback
          error={error}
          onRetry={reset}
          onGoHome={() => {
            // Navigating changes `resetKey`, which clears the boundary.
            if (location.pathname !== HOME_ROUTE) {
              navigate(HOME_ROUTE);
              return;
            }
            // Already on the home route, so there is no route change to react
            // to. Clearing the boundary here would just re-render the same
            // crash and leave both buttons doing the same thing, with a manual
            // browser reload as the only way out. Load the page instead: it is
            // a real escape, and it drops any in-memory state a soft reset
            // would otherwise carry back into the crash.
            window.location.assign(HOME_ROUTE);
          }}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

/**
 * Last-resort boundary mounted above the Router, so that a failure in the
 * router or in a top-level provider still produces a readable page rather than
 * a blank one. It cannot navigate - there is no Router context above it - so
 * recovery here is a real page load.
 */
export function TopLevelErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={({ error, reset }) => (
        <AppErrorFallback
          error={error}
          onRetry={reset}
          onGoHome={() => window.location.assign(HOME_ROUTE)}
        />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}
